/**
 * HTTP Client - Lightweight, typed API client built on fetch
 * Hỗ trợ cả browser và Next.js server environments
 */

import type { ApiOptions, RequestOptions, HttpMethod } from '../types';
import { ApiError } from '../types';

function buildQueryString(
  query?: Record<string, string | number | boolean | undefined | null>
): string {
  if (!query) return '';
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    searchParams.append(key, String(value));
  }

  const qs = searchParams.toString();

  return qs ? `?${qs}` : '';
}

function getContentType(headers: Headers): string {
  return (headers.get('content-type') || '').toLowerCase();
}

function isJsonContentType(contentType: string): boolean {
  return (
    contentType.includes('application/json') || contentType.includes('+json')
  );
}

function releaseUnreadResponse(response: Response | undefined): void {
  if (response?.body && !response.bodyUsed) {
    // Release discarded streams without delaying the retry or masking its error.
    void response.body.cancel().catch(() => undefined);
  }
}

/**
 * Tạo hash đơn giản từ body để dùng làm key cho deduplication
 */
function hashBody(body: unknown): string {
  if (body === undefined || body === null) return '';

  try {
    if (typeof body === 'string') return body;
    if (body instanceof FormData) {
      // FormData không thể hash chính xác, dùng timestamp
      return `formdata-${Date.now()}`;
    }
    if (body instanceof Blob || body instanceof ArrayBuffer) {
      // Binary data không thể hash, dùng timestamp
      return `binary-${Date.now()}`;
    }

    return JSON.stringify(body);
  } catch {
    return String(body);
  }
}

/**
 * Tạo request key để deduplicate requests
 */
function createRequestKey(
  method: HttpMethod,
  path: string,
  query: string,
  body: unknown
): string {
  const bodyHash = hashBody(body);

  return `${method}:${path}:${query}:${bodyHash}`;
}

/**
 * HTTP Client class với token refresh tự động và request deduplication
 */
export class HttpClient {
  private readonly baseUrl: string | (() => string);
  private readonly defaultHeaders: Record<string, string>;
  private readonly getAuthToken?: ApiOptions['getAuthToken'];
  private readonly defaultTimeoutMs: number;
  private readonly onUnauthorized?: ApiOptions['onUnauthorized'];
  private readonly refreshToken?: ApiOptions['refreshToken'];
  private readonly name?: string;
  private readonly credentials?: RequestCredentials;
  private isRefreshing = false; // Flag để tránh nhiều request refresh đồng thời
  // Map để track các pending requests - tránh duplicate calls
  private readonly pendingRequests = new Map<string, Promise<unknown>>();

  constructor(options: ApiOptions) {
    this.baseUrl =
      typeof options.baseUrl === 'function'
        ? options.baseUrl
        : options.baseUrl.replace(/\/$/, '');
    this.defaultHeaders = options.defaultHeaders || {
      'Content-Type': 'application/json',
    };
    this.getAuthToken = options.getAuthToken;
    this.defaultTimeoutMs = options.defaultTimeoutMs ?? 60 * 1000;
    this.onUnauthorized = options.onUnauthorized;
    this.refreshToken = options.refreshToken;
    this.name = options.name;
    this.credentials = options.credentials;
  }

  /**
   * Lấy base URL động (hỗ trợ cả string và function)
   */
  private getBaseUrl(): string {
    return typeof this.baseUrl === 'function' ? this.baseUrl() : this.baseUrl;
  }

  /**
   * Kết hợp nhiều AbortSignal thành một signal duy nhất
   */
  private composeSignals(signals: AbortSignal[]): {
    cleanup: () => void;
    signal: AbortSignal | undefined;
  } {
    if (signals.length === 0) {
      return { cleanup: () => undefined, signal: undefined };
    }
    if (signals.length === 1) {
      return { cleanup: () => undefined, signal: signals[0] };
    }

    const controller = new AbortController();
    const listeningSignals: AbortSignal[] = [];
    const cleanup = () => {
      listeningSignals.forEach(signal => {
        signal.removeEventListener('abort', abort);
      });
      listeningSignals.length = 0;
    };
    const abort = () => {
      controller.abort();
      cleanup();
    };

    signals.forEach(signal => {
      if (controller.signal.aborted) return;

      if (signal.aborted) {
        abort();
      } else {
        signal.addEventListener('abort', abort, { once: true });
        listeningSignals.push(signal);
      }
    });

    return { cleanup, signal: controller.signal };
  }

  /**
   * Tạo request init chuẩn hoá, đảm bảo tái sử dụng cho việc retry
   */
  private createRequestInit(
    method: HttpMethod,
    headers: Record<string, string>,
    body: BodyInit | undefined,
    signal?: AbortSignal
  ): RequestInit {
    return {
      method,
      headers,
      body,
      signal,
      // Sử dụng credentials từ config, mặc định là 'include' để gửi cookies
      // 'omit' hoặc 'same-origin' có thể giúp tránh sec-fetch-storage-access header
      credentials: this.credentials ?? 'include',
    };
  }

  /**
   * Parse dữ liệu thành công dựa trên cấu hình responseType
   */
  private async parseSuccessfulResponse<T>(
    response: Response,
    requestedType: NonNullable<RequestOptions['responseType']>
  ): Promise<T> {
    if (requestedType === 'json') {
      return (await response.json()) as T;
    }
    if (requestedType === 'text') {
      return (await response.text()) as unknown as T;
    }
    if (requestedType === 'blob') {
      return (await response.blob()) as unknown as T;
    }
    if (requestedType === 'arrayBuffer') {
      return (await response.arrayBuffer()) as unknown as T;
    }

    // Auto detect
    const contentType = getContentType(response.headers);

    if (isJsonContentType(contentType)) {
      return (await response.json()) as T;
    }

    if (
      contentType.includes('application/pdf') ||
      contentType.includes('application/octet-stream') ||
      contentType.startsWith('image/')
    ) {
      return (await response.blob()) as unknown as T;
    }

    return (await response.text()) as unknown as T;
  }

  /**
   * Parse body lỗi dựa trên content-type
   */
  private async parseErrorBody(response: Response): Promise<unknown> {
    const contentType = getContentType(response.headers);

    try {
      if (isJsonContentType(contentType)) {
        return await response.json();
      }

      return await response.text();
    } catch {
      return null;
    }
  }

  /**
   * Sinh ra ApiError chuẩn hoá với đầy đủ metadata
   */
  private async buildApiError(
    response: Response,
    url: string
  ): Promise<ApiError> {
    const errorBody = await this.parseErrorBody(response);

    return new ApiError({
      message: `Request failed with status ${response.status}`,
      status: response.status,
      statusText: response.statusText,
      body: (errorBody as unknown) ?? null,
      url,
    });
  }

  /**
   * Gọi callback onUnauthorized nếu được cấu hình
   */
  private async safeOnUnauthorized() {
    if (!this.onUnauthorized) return;

    try {
      await this.onUnauthorized();
    } catch {
      // Bỏ qua lỗi secondary
    }
  }

  /**
   * Chờ refresh token hiện tại hoàn tất (dành cho các request chạy song song)
   */
  private async waitForRefreshCompletion() {
    const MAX_ATTEMPTS = 20;
    let attempts = 0;

    while (this.isRefreshing && attempts < MAX_ATTEMPTS) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts += 1;
    }
  }

  /**
   * Thử refresh token và retry request khi gặp 401
   */
  private async handleUnauthorizedAndRetry(
    url: string,
    method: HttpMethod,
    headers: Record<string, string>,
    requestBody: BodyInit | undefined,
    signal: AbortSignal | undefined
  ): Promise<Response | null> {
    if (!this.refreshToken) return null;

    if (!this.isRefreshing) {
      this.isRefreshing = true;

      try {
        const refreshSuccess = await this.refreshToken();

        if (!refreshSuccess) {
          return null;
        }
      } catch (error) {
        // Lỗi mạng/429/5xx khi refresh không chứng minh phiên đã hết hạn.
        // Propagate để caller hiển thị lỗi và giữ refresh token cho lần thử sau.
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    } else {
      await this.waitForRefreshCompletion();
    }

    if (this.getAuthToken) {
      const newToken = await this.getAuthToken();

      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
      } else {
        return null;
      }
    }

    // The request owns this signal until body parsing completes, not just headers.
    return fetch(
      url,
      this.createRequestInit(method, headers, requestBody, signal)
    );
  }

  /**
   * Main request method với request deduplication
   * Nếu có nhiều requests giống nhau đang pending, chỉ gọi 1 lần và share kết quả
   */
  async request<TResponse = unknown, TBody = unknown>(
    options: RequestOptions<TBody>
  ): Promise<TResponse> {
    const method: HttpMethod =
      options.method || (options.body ? 'POST' : 'GET');
    const query = buildQueryString(options.query);
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${options.path}${query}`;

    // Tạo request key để deduplicate
    // Chỉ deduplicate GET requests hoặc requests không có body unique (FormData/Blob)
    const shouldDeduplicate =
      !options.signal &&
      (method === 'GET' ||
        (options.body !== undefined &&
          options.body !== null &&
          !(options.body instanceof FormData) &&
          !(options.body instanceof Blob) &&
          !(options.body instanceof ArrayBuffer)));

    const requestKey = shouldDeduplicate
      ? createRequestKey(method, options.path, query, options.body)
      : null;

    // Kiểm tra nếu có request đang pending với cùng key
    if (requestKey && this.pendingRequests.has(requestKey)) {
      const pendingPromise = this.pendingRequests.get(requestKey);

      // Return cùng Promise - tất cả callers sẽ nhận cùng kết quả
      return pendingPromise as Promise<TResponse>;
    }

    // Tạo Promise mới cho request này
    const requestPromise = this.executeRequest<TResponse, TBody>(
      options,
      method,
      query,
      url
    );

    // Lưu vào Map nếu cần deduplicate
    if (requestKey) {
      this.pendingRequests.set(requestKey, requestPromise);

      // Xóa khỏi Map khi request hoàn tất (success hoặc error)
      requestPromise
        .then(() => {
          this.pendingRequests.delete(requestKey);
        })
        .catch(() => {
          this.pendingRequests.delete(requestKey);
        });
    }

    return requestPromise;
  }

  /**
   * Thực thi request thực tế
   */
  private async executeRequest<TResponse = unknown, TBody = unknown>(
    options: RequestOptions<TBody>,
    method: HttpMethod,
    query: string,
    url: string
  ): Promise<TResponse> {
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...(options.headers || {}),
    };

    // Attach Authorization header nếu có và không skip auth
    if (this.getAuthToken && !options.skipAuth) {
      const token = await this.getAuthToken();

      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutMs = options.timeoutMs ?? this.defaultTimeoutMs;

    // Detect special body types
    const isFormDataBody =
      typeof FormData !== 'undefined' && options.body instanceof FormData;
    const isBlobBody =
      typeof Blob !== 'undefined' && options.body instanceof Blob;
    const isArrayBufferBody =
      typeof ArrayBuffer !== 'undefined' && options.body instanceof ArrayBuffer;

    // Normalize header keys
    const headerKeys = Object.keys(headers);
    const contentTypeKey = headerKeys.find(
      key => key.toLowerCase() === 'content-type'
    );

    // Nếu gửi FormData, để browser tự set boundary
    if (isFormDataBody) {
      if (contentTypeKey) delete headers[contentTypeKey];
    }

    // Build request payload
    const requestBody =
      options.body !== undefined && options.body !== null
        ? isFormDataBody ||
          isBlobBody ||
          isArrayBufferBody ||
          typeof options.body === 'string'
          ? (options.body as unknown as BodyInit)
          : (() => {
              if (!contentTypeKey) headers['Content-Type'] = 'application/json';

              return JSON.stringify(options.body);
            })()
        : undefined;

    const signalList: AbortSignal[] = [controller.signal];

    if (options.signal) {
      signalList.push(options.signal);
    }

    const initialSignal = this.composeSignals(signalList);
    const initialRequestInit = this.createRequestInit(
      method,
      headers,
      requestBody,
      initialSignal.signal
    );

    let response: Response | undefined;

    try {
      // Acquire the timer only after serialization, which can throw.
      if (timeoutMs && timeoutMs > 0) {
        timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      }
      response = await fetch(url, initialRequestInit);

      if (response.status === 401 && !options.skipAuth) {
        const retriedResponse = await this.handleUnauthorizedAndRetry(
          url,
          method,
          headers,
          requestBody,
          initialSignal.signal
        );

        if (retriedResponse) {
          releaseUnreadResponse(response);
          response = retriedResponse;
        }
      }

      if (!response.ok) {
        if (response.status === 401 && !options.skipAuth) {
          await this.safeOnUnauthorized();
        }
        throw await this.buildApiError(response, url);
      }

      if (response.status === 204) {
        return undefined as unknown as TResponse;
      }

      const requestedType = options.responseType ?? 'auto';

      return await this.parseSuccessfulResponse<TResponse>(
        response,
        requestedType
      );
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        if (options.signal?.aborted) {
          throw error;
        }

        const baseUrl = this.getBaseUrl();

        throw new ApiError({
          message: 'Request timed out',
          status: 408,
          statusText: 'Request Timeout',
          body: options.body,
          url: `${baseUrl}${options.path}`,
        });
      }
      throw error;
    } finally {
      releaseUnreadResponse(response);
      initialSignal.cleanup();
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * GET request
   * Có thể gọi theo 2 cách:
   * - get(path, { skipAuth: true }) // Không có query
   * - get(path, { id: 1 }, { skipAuth: true }) // Có query
   */
  get<TResponse = unknown>(
    path: string,
    queryOrOptions?:
      | RequestOptions['query']
      | Omit<RequestOptions, 'path' | 'method' | 'query'>,
    opts?: Omit<RequestOptions, 'path' | 'method' | 'query'>
  ) {
    // Nếu tham số thứ 2 có skipAuth thì đó là options, không phải query
    // skipAuth là key đặc trưng của RequestOptions, không bao giờ xuất hiện trong query params
    const isOptionsOnly =
      queryOrOptions != null &&
      typeof queryOrOptions === 'object' &&
      !Array.isArray(queryOrOptions) &&
      'skipAuth' in queryOrOptions;

    const query = isOptionsOnly
      ? undefined
      : (queryOrOptions as RequestOptions['query']);
    const finalOptions = isOptionsOnly
      ? (queryOrOptions as Omit<RequestOptions, 'path' | 'method' | 'query'>)
      : opts || {};

    return this.request<TResponse>({
      ...finalOptions,
      method: 'GET',
      path,
      query,
    });
  }

  /**
   * POST request
   */
  post<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    opts?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>({
      ...opts,
      method: 'POST',
      path,
      body,
    });
  }

  /**
   * PUT request
   */
  put<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    opts?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>({
      ...opts,
      method: 'PUT',
      path,
      body,
    });
  }

  /**
   * PATCH request
   */
  patch<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    opts?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>({
      ...opts,
      method: 'PATCH',
      path,
      body,
    });
  }

  /**
   * DELETE request
   */
  delete<TResponse = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    opts?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'>
  ) {
    return this.request<TResponse, TBody>({
      ...opts,
      method: 'DELETE',
      path,
      body,
    });
  }
}
